import { Button, Divider, Dropdown, InputNumber, Slider, Switch } from 'antd';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import { colorConstants } from 'config/colorConstants';
import { WhitespaceAxisRadioGroup } from '../WhitespaceAnalysisHeader';

const FILTER_BUTTON_CLASS =
    '!h-8 !rounded-md !border-black/15 !bg-white !px-3 !text-[14px] !font-normal !text-blackTitle !flex !justify-center !items-center';

const INCREMENT_SLIDER_COLOR = colorConstants.violetAlt1;

type WhitespaceAnalysisToolbarProps = {
    xAxisOptions: string[];
    yAxisOptions: string[];
    crossSection: string;
    crossSectionOptions: string[];
    incrementConfig: { min: number; max: number; step: number; label: string };
    showWhitespaces: boolean;
    pendingXAxis: string;
    pendingYAxis: string;
    isAxesPopoverOpen: boolean;
    isCrossSectionOpen: boolean;
    showDollarPrefix: boolean;
    localIncrementValue: number;
    onAxesPopoverOpenChange: (open: boolean) => void;
    onPendingXAxisChange: (value: string) => void;
    onPendingYAxisChange: (value: string) => void;
    onApplyAxes: () => void;
    onCrossSectionOpenChange: (open: boolean) => void;
    onCrossSectionSelect: (option: string) => void;
    onIncrementChange: (value: number | [number, number]) => void;
    onIncrementInputChange: (value: number | null) => void;
    onIncrementInputParser: (displayValue: string | undefined) => number;
    onShowWhitespacesChange: (checked: boolean) => void;
};

const WhitespaceAnalysisToolbar = ({
    xAxisOptions,
    yAxisOptions,
    crossSection,
    crossSectionOptions,
    incrementConfig,
    showWhitespaces,
    pendingXAxis,
    pendingYAxis,
    isAxesPopoverOpen,
    isCrossSectionOpen,
    showDollarPrefix,
    localIncrementValue,
    onAxesPopoverOpenChange,
    onPendingXAxisChange,
    onPendingYAxisChange,
    onApplyAxes,
    onCrossSectionOpenChange,
    onCrossSectionSelect,
    onIncrementChange,
    onIncrementInputChange,
    onIncrementInputParser,
    onShowWhitespacesChange,
}: WhitespaceAnalysisToolbarProps) => {
    const axesPopoverContent = (
        <div className="w-[360px] rounded-xl border border-solid border-borderSecondary bg-white p-4 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-start gap-5">
                <WhitespaceAxisRadioGroup
                    label="X-Axis"
                    value={pendingXAxis}
                    options={xAxisOptions}
                    onChange={onPendingXAxisChange}
                />
                <Divider type="vertical" className="!h-[124px] !m-0" />
                <WhitespaceAxisRadioGroup
                    label="Y-Axis"
                    value={pendingYAxis}
                    options={yAxisOptions}
                    onChange={onPendingYAxisChange}
                />
            </div>
            <Divider className="!my-4" />
            <div className="flex justify-end">
                <Button
                    type="primary"
                    className="!h-8 !rounded-md !border-purpleFont !bg-purpleFont !px-4 !font-medium"
                    onClick={onApplyAxes}
                >
                    Apply
                </Button>
            </div>
        </div>
    );

    const crossSectionDropdownContent = (
        <div className="w-[160px] rounded-lg bg-white p-1 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
            {crossSectionOptions.map((option) => {
                const isSelected = crossSection === option;
                return (
                    <button
                        key={option}
                        type="button"
                        className={`flex h-8 w-full items-center rounded px-3 text-left text-[14px] leading-[22px] ${isSelected ? 'font-semibold text-purpleFont' : 'font-normal text-blackTitle'}`}
                        style={isSelected ? { backgroundColor: colorConstants.axisSelectedBg } : undefined}
                        onClick={() => onCrossSectionSelect(option)}
                    >
                        {option}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="mt-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
                <Dropdown
                    trigger={['click']}
                    placement="bottomLeft"
                    open={isAxesPopoverOpen}
                    onOpenChange={onAxesPopoverOpenChange}
                    dropdownRender={() => axesPopoverContent}
                >
                    <Button className={FILTER_BUTTON_CLASS}>
                        <span className="text-fontGray mr-2">Axes</span>
                        <HeaderSortDescChevron color={colorConstants.blackTitle} />
                    </Button>
                </Dropdown>
                <Dropdown
                    trigger={['click']}
                    placement="bottomLeft"
                    open={isCrossSectionOpen}
                    onOpenChange={onCrossSectionOpenChange}
                    dropdownRender={() => crossSectionDropdownContent}
                >
                    <Button className={FILTER_BUTTON_CLASS}>
                        <span className="text-fontGray mr-1">Cross Section:</span>
                        <span className="text-fontGray mr-2">{crossSection}</span>
                        <HeaderSortDescChevron color={colorConstants.blackTitle} />
                    </Button>
                </Dropdown>
                <div className="flex items-center gap-2">
                    <span className="text-[14px] leading-[22px] text-blackTitle">{incrementConfig.label}</span>
                    <div className="w-[80px]">
                        <Slider
                            min={incrementConfig.min}
                            max={incrementConfig.max}
                            step={incrementConfig.step}
                            value={localIncrementValue}
                            onChange={onIncrementChange}
                            railStyle={{ backgroundColor: INCREMENT_SLIDER_COLOR }}
                            trackStyle={{ backgroundColor: INCREMENT_SLIDER_COLOR }}
                            handleStyle={{ borderColor: INCREMENT_SLIDER_COLOR }}
                        />
                    </div>
                    <InputNumber
                        min={incrementConfig.min}
                        max={incrementConfig.max}
                        step={incrementConfig.step}
                        value={localIncrementValue}
                        prefix={showDollarPrefix ? '$' : undefined}
                        onChange={onIncrementInputChange}
                        controls={false}
                        parser={onIncrementInputParser}
                        className="!flex !h-8 !w-[80px] !items-center !overflow-hidden !rounded-md [&_.ant-input-number]:!h-full [&_.ant-input-number]:!border-none [&_.ant-input-number]:!bg-transparent [&_.ant-input-number]:!shadow-none [&_.ant-input-number-input]:!h-full [&_.ant-input-number-input]:!text-[14px] [&_.ant-input-number-input]:!leading-[22px] [&_.ant-input-number-input]:!text-blackTitle"
                    />
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Switch size="small" checked={showWhitespaces} onChange={onShowWhitespacesChange} />
                <span className="text-[14px] leading-[22px] text-blackTitle">Show Whitespaces</span>
            </div>
        </div>
    );
};

export default WhitespaceAnalysisToolbar;
